import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {CurrencyPipe} from '@angular/common';
import {ConfirmationService, MessageService} from 'primeng/api';
import {EligibleOfferSetModel, OfferSetOfferModel} from '../../../../../model/eligible-offer-set.model';
import {
  EditableOfferRule,
  NormalizedWritableOfferRule,
  OfferRuleChangeSet,
  OfferRuleChangeSetValidation,
  ruleChangeStates,
  RuleChangeState
} from '../../../../../model/explicit-save-offer-rule.model';
import {OfferRuleModel} from '../../../../../model/offer-rule.model';
import {OfferRuleService} from '../../../../../service/offer-rule.service';
import {BulkOfferRuleData} from '../../../../../model/bulk-offer-rule.model';

const persistedClientKeyPrefix = 'persisted-';
const draftClientKeyPrefix = 'draft-';
const maximumBulkOperations = 50;
const uuidByteLength = 16;
const uuidVersionIndex = 6;
const uuidVariantIndex = 8;
const uuidVersionMask = 15;
const uuidVersionValue = 64;
const uuidVariantMask = 63;
const uuidVariantValue = 128;
const uuidSegmentLengths = [4, 2, 2, 2, 6];
const ruleTypeAll = 'all';
const ruleTypeOfferSet = 'offerSetRule';
const ruleTypeDiscount = 'discountRule';

@Component({
  selector: 'auto-ipop-explicit-save-offer-set-rules',
  templateUrl: './ipop-explicit-save-offer-set-rules.component.html',
  styleUrls: ['./ipop-explicit-save-offer-set-rules.component.scss'],
  standalone: false,
  providers: [CurrencyPipe]
})
export class IpopExplicitSaveOfferSetRulesComponent implements OnInit, OnChanges {
  @Input() accountId: number;
  @Input() isOfferRegistryProcessingActive = false;
  @Input() isProcessingRules = false;
  @Input() isSavingRules = false;
  @Input() areRuleActionsExternallyDisabled = false;
  @Input() isProcessRulesButtonDisabled = false;
  @Input() pendingGeneratedRuleDraft: OfferRuleModel | null = null;

  @Output() processRulesRequested = new EventEmitter<void>();
  @Output() saveRulesRequested = new EventEmitter<OfferRuleChangeSet>();
  @Output() hasUnsavedRuleChangesChange = new EventEmitter<boolean>();
  @Output() generatedRuleDraftHandled = new EventEmitter<void>();

  publicRules: EditableOfferRule[] = [];
  privateRules: EditableOfferRule[] = [];
  filteredPublicRules: EditableOfferRule[] = [];
  filteredPrivateRules: EditableOfferRule[] = [];
  eligibleOfferSets: EligibleOfferSetModel[] = [];
  offerSetOfferTextMap: {[key: number]: string} = {};
  tableLoading = false;
  dialogVisible = false;
  dialogTitle = '';
  dialogMode: 'create' | 'edit' = 'create';
  dialogRuleType = ruleTypeOfferSet;
  isGeneratedPrefill = false;
  selectedRule: OfferRuleModel | null = null;
  searchValue = '';
  selectedRuleTypeFilter = ruleTypeAll;
  hasLoaded = false;
  hasUnsavedRuleChanges = false;
  changeSet: OfferRuleChangeSet = this.createEmptyChangeSet();
  changeSetValidation: OfferRuleChangeSetValidation = this.createInitialValidation();
  ruleTypeFilterOptions = [
    {label: 'All', value: ruleTypeAll},
    {label: 'Offer Set Rules', value: ruleTypeOfferSet},
    {label: 'Dollar Discounts', value: ruleTypeDiscount}
  ];

  private baselineById = new Map<number, OfferRuleModel>();
  private workingRowsByClientKey = new Map<string, EditableOfferRule>();
  private selectedClientKey: string | null = null;
  private loadedAccountId: number | null = null;
  private rulesLoadedAccountId: number | null = null;
  private eligibleOfferSetsLoadedAccountId: number | null = null;
  private generatedRuleDraftPending: OfferRuleModel | null = null;
  private generatedRuleDialogActive = false;
  private accountLoadSequence = 0;
  private rulesLoadFailed = false;
  private eligibleOfferSetsLoadFailed = false;

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private offerRuleService: OfferRuleService,
    private currencyPipe: CurrencyPipe
  ) {
  }

  ngOnInit(): void {
    this.handleAccountChange(this.accountId);
  }

  /**
   * Responds to account, generated-draft, and action-lock input changes.
   */
  ngOnChanges(changes: SimpleChanges): void
  {
    const accountChange = changes['accountId'];
    const generatedRuleDraftChange = changes['pendingGeneratedRuleDraft'];
    const actionLockChange = changes['areRuleActionsExternallyDisabled'];
    if (accountChange) {
      this.handleAccountChange(accountChange.currentValue);
    }
    if (generatedRuleDraftChange) {
      this.handlePendingGeneratedRuleDraft(generatedRuleDraftChange.currentValue);
    }
    if (actionLockChange) {
      this.openGeneratedRuleDialogWhenReady();
    }
  }

  /**
   * Accepts an account-bound generated draft for deferred modal display.
   */
  private handlePendingGeneratedRuleDraft(rule: OfferRuleModel | null): void {
    let accepted = false;
    if (rule !== null && Number.isInteger(this.accountId) && this.accountId > 0 && rule.accountId === this.accountId) {
      this.generatedRuleDraftPending = this.cloneWritableRule(rule);
      this.openGeneratedRuleDialogWhenReady();
      if (this.rulesLoadFailed || this.eligibleOfferSetsLoadFailed) {
        this.discardPendingGeneratedRuleDraft();
      }
      accepted = true;
    }
    if (rule !== null && !accepted) {
      this.generatedRuleDraftHandled.emit();
    }
    if (rule === null && !this.generatedRuleDialogActive) {
      this.generatedRuleDraftPending = null;
    }
  }

  /**
   * Opens a pending generated draft after dependencies load and actions unlock.
   */
  private openGeneratedRuleDialogWhenReady(): void
  {
    const dependenciesReady = this.rulesLoadedAccountId === this.accountId
      && this.eligibleOfferSetsLoadedAccountId === this.accountId;
    const canOpenDialog = this.generatedRuleDraftPending !== null
      && dependenciesReady
      && !this.generatedRuleDialogActive
      && !this.getRuleMutationDisabled();
    if (canOpenDialog) {
      this.dialogMode = 'create';
      this.dialogRuleType = ruleTypeOfferSet;
      this.dialogTitle = 'Create Offer Set Rule';
      this.selectedClientKey = null;
      this.selectedRule = this.cloneWritableRule(this.generatedRuleDraftPending);
      this.generatedRuleDraftPending = null;
      this.isGeneratedPrefill = true;
      this.generatedRuleDialogActive = true;
      this.dialogVisible = true;
    }
  }

  /**
   * Discards an unopenable generated draft and reports it as handled.
   */
  private discardPendingGeneratedRuleDraft(): void {
    if (this.generatedRuleDraftPending !== null) {
      this.generatedRuleDraftPending = null;
      this.generatedRuleDraftHandled.emit();
    }
  }

  /**
   * Requests account rule processing when the action is available.
   */
  requestProcessRules(): void {
    if (!this.isProcessRulesButtonDisabled) {
      this.processRulesRequested.emit();
    }
  }

  /**
   * Emits an isolated snapshot of the valid local change set.
   */
  requestSaveRules(): void {
    if (!this.getSaveRulesButtonDisabled()) {
      const changeSetSnapshot = this.cloneChangeSet(this.changeSet);
      this.saveRulesRequested.emit(changeSetSnapshot);
    }
  }

  /**
   * Checks whether local rule changes are eligible for bulk save.
   */
  getSaveRulesButtonDisabled(): boolean {
    const invalidAccountId = !Number.isInteger(this.accountId) || this.accountId <= 0;
    const isDisabled = !this.hasLoaded
      || !this.hasUnsavedRuleChanges
      || !this.changeSetValidation.isValid
      || this.changeSetValidation.operationCount > maximumBulkOperations
      || this.getRuleMutationDisabled()
      || invalidAccountId;

    return isDisabled;
  }

  /**
   * Checks whether processing, saving, or external state locks mutations.
   */
  getRuleMutationDisabled(): boolean {
    const isDisabled = this.isOfferRegistryProcessingActive
      || this.isProcessingRules
      || this.isSavingRules
      || this.areRuleActionsExternallyDisabled;

    return isDisabled;
  }

  /**
   * Replaces the working state with validated authoritative rules.
   */
  replaceWithAuthoritativeRules(rules: OfferRuleModel[]): boolean {
    const validationErrors = this.getLoadedRuleErrors(rules);
    let wasReplaced = false;
    if (validationErrors.length === 0) {
      this.closeDialog();
      this.baselineById = new Map<number, OfferRuleModel>();
      this.workingRowsByClientKey = new Map<string, EditableOfferRule>();
      for (const rule of rules) {
        this.addLoadedRule(rule);
      }
      this.hasLoaded = true;
      this.refreshDerivedState(true);
      wasReplaced = true;
    }
    if (validationErrors.length > 0) {
      this.hasLoaded = false;
      this.showLoadError('Saved offer rules could not be prepared from the authoritative response.');
    }

    return wasReplaced;
  }

  /**
   * Applies a new text search to both rule lists.
   */
  onSearch(searchTerm: string): void {
    this.searchValue = searchTerm;
    this.applyFilters();
  }

  /**
   * Applies a new rule-type filter to both rule lists.
   */
  onRuleTypeFilterChange(filterValue: string): void {
    this.selectedRuleTypeFilter = filterValue;
    this.applyFilters();
  }

  /**
   * Opens an unlocked dialog for a new local rule.
   */
  openCreateDialog(ruleType: string): void {
    if (!this.getRuleMutationDisabled()) {
      this.dialogMode = 'create';
      this.dialogRuleType = ruleType;
      this.dialogTitle = 'Create Offer Set Rule';
      this.selectedClientKey = null;
      this.selectedRule = null;
      this.isGeneratedPrefill = false;
      this.generatedRuleDialogActive = false;
      this.dialogVisible = true;
    }
  }

  /**
   * Opens an unlocked persisted or local rule for editing.
   */
  openEditDialog(row: EditableOfferRule): void {
    const canEdit = !this.getRuleMutationDisabled() && row.changeState !== ruleChangeStates.deleted;
    if (canEdit) {
      this.dialogMode = 'edit';
      this.dialogRuleType = this.getRuleTypeFromRule(row.rule);
      this.dialogTitle = 'Edit Offer Set Rule';
      this.selectedClientKey = row.clientKey;
      this.selectedRule = this.cloneRule(row.rule);
      this.isGeneratedPrefill = false;
      this.generatedRuleDialogActive = false;
      this.dialogVisible = true;
    }
  }

  /**
   * Resets dialog state and completes any generated-draft handoff.
   */
  closeDialog(): void {
    const generatedRuleWasActive = this.generatedRuleDialogActive;
    this.dialogVisible = false;
    this.selectedClientKey = null;
    this.selectedRule = null;
    this.isGeneratedPrefill = false;
    this.generatedRuleDialogActive = false;
    if (generatedRuleWasActive) {
      this.generatedRuleDraftHandled.emit();
    }
  }

  /**
   * Applies a submitted modal rule to the local working state.
   */
  handleDialogSubmitted(rule: OfferRuleModel): void {
    if (!this.getRuleMutationDisabled()) {
      if (this.dialogMode === 'create') {
        this.addCreatedRule(rule);
      }
      if (this.dialogMode === 'edit') {
        this.applyEditedRule(rule);
      }
      this.closeDialog();
    }
  }

  /**
   * Confirms and stages a local rule deletion while mutations are unlocked.
   */
  deleteRule(row: EditableOfferRule): void {
    const canDelete = !this.getRuleMutationDisabled() && row.changeState !== ruleChangeStates.deleted;
    if (canDelete) {
      this.confirmationService.confirm({
        message: 'Are you sure you want to delete this rule?',
        header: 'Delete Rule',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          if (!this.getRuleMutationDisabled()) {
            this.applyLocalDelete(row.clientKey);
          }
        }
      });
    }
  }

  /**
   * Restores a persisted row marked for deletion.
   */
  undoDelete(row: EditableOfferRule): void {
    if (!this.getRuleMutationDisabled() && row.changeState === ruleChangeStates.deleted) {
      this.restorePersistedRow(row.clientKey);
    }
  }

  /**
   * Reverts a created or updated row to its prior local state.
   */
  revertRule(row: EditableOfferRule): void {
    if (!this.getRuleMutationDisabled()) {
      if (row.changeState === ruleChangeStates.created) {
        this.workingRowsByClientKey.delete(row.clientKey);
        this.refreshDerivedState();
      }
      if (row.changeState === ruleChangeStates.updated) {
        this.restorePersistedRow(row.clientKey);
      }
    }
  }

  /**
   * Provides the display label for a local change state.
   */
  getChangeStateText(changeState: RuleChangeState): string {
    let label = '';
    if (changeState === ruleChangeStates.created) {
      label = 'New';
    }
    if (changeState === ruleChangeStates.updated) {
      label = 'Modified';
    }
    if (changeState === ruleChangeStates.deleted) {
      label = 'Pending deletion';
    }

    return label;
  }

  /**
   * Resolves the display name for an Offer Set or discount rule.
   */
  getOfferSetName(rule: OfferRuleModel): string {
    let name = 'Dollar Discount';
    const offerSetId = this.normalizeNumber(rule.offerSetId);
    if (offerSetId !== null) {
      const matchingOfferSet = this.eligibleOfferSets.find((offerSet) => offerSet.id === offerSetId);
      if (matchingOfferSet && matchingOfferSet.title) {
        name = matchingOfferSet.title;
      }
      if (!matchingOfferSet || !matchingOfferSet.title) {
        name = `Offer Set ${offerSetId}`;
      }
    }

    return name;
  }

  /**
   * Builds the Offer Set editor link for a persisted selection.
   */
  getOfferSetLink(rule: OfferRuleModel): string | null {
    let link: string | null = null;
    const offerSetId = this.normalizeNumber(rule.offerSetId);
    if (offerSetId !== null && this.accountId > 0) {
      link = `/new-specials/${this.accountId}#/specials/edit/${offerSetId}`;
    }

    return link;
  }

  /**
   * Builds non-empty vehicle criteria labels for display.
   */
  getVehicleCriteriaTags(rule: OfferRuleModel): string[] {
    const tags: string[] = [];
    this.addCriteriaTag(tags, rule.vehicleCondition);
    this.addCriteriaTag(tags, rule.year);
    this.addCriteriaTag(tags, rule.make);
    this.addCriteriaTag(tags, rule.model);
    this.addCriteriaTag(tags, rule.trim);
    if (tags.length === 0) {
      tags.push('Any vehicle');
    }

    return tags;
  }

  /**
   * Resolves the CSS class for the normalized rule status.
   */
  getRuleStatusClass(rule: OfferRuleModel): string {
    let statusClass = 'active';
    if (this.normalizeStatus(rule.status) === 'Inactive') {
      statusClass = 'inactive';
    }

    return statusClass;
  }

  /**
   * Resolves the normalized rule status label.
   */
  getRuleStatusLabel(rule: OfferRuleModel): string {
    const status = this.normalizeStatus(rule.status);

    return status;
  }

  /**
   * Formats the latest valid rule timestamp for display.
   */
  getLatestTimestampDisplay(rule: OfferRuleModel): string {
    let display = '';
    const timestamp = rule.modified || rule.created;
    if (timestamp) {
      const date = new Date(timestamp);
      if (!Number.isNaN(date.getTime())) {
        display = date.toLocaleString();
      }
    }

    return display;
  }

  /**
   * Resets and reloads local state when the account changes.
   */
  private handleAccountChange(accountValue: number): void {
    const validAccountId = Number.isInteger(accountValue) && accountValue > 0;
    const isNewAccount = this.loadedAccountId !== accountValue;
    if (isNewAccount) {
      this.accountLoadSequence += 1;
      this.loadedAccountId = null;
      if (validAccountId) {
        this.loadedAccountId = accountValue;
      }
      this.resetState();
      if (validAccountId) {
        this.loadEligibleOfferSets();
        this.loadRules();
      }
    }
  }

  /**
   * Clears all account-scoped rules, filters, loading, and dialog state.
   */
  private resetState(): void {
    const generatedRuleWasPending = this.generatedRuleDraftPending !== null;
    this.baselineById = new Map<number, OfferRuleModel>();
    this.workingRowsByClientKey = new Map<string, EditableOfferRule>();
    this.eligibleOfferSets = [];
    this.offerSetOfferTextMap = {};
    this.rulesLoadedAccountId = null;
    this.eligibleOfferSetsLoadedAccountId = null;
    this.rulesLoadFailed = false;
    this.eligibleOfferSetsLoadFailed = false;
    this.generatedRuleDraftPending = null;
    this.searchValue = '';
    this.selectedRuleTypeFilter = ruleTypeAll;
    this.hasLoaded = false;
    this.closeDialog();
    if (generatedRuleWasPending) {
      this.generatedRuleDraftHandled.emit();
    }
    this.refreshDerivedState(true);
  }

  /**
   * Loads eligible Offer Sets for the current account.
   */
  private loadEligibleOfferSets(): void {
    const requestedAccountId = this.accountId;
    const loadSequence = this.accountLoadSequence;
    this.offerRuleService.getEligibleOfferSets(requestedAccountId).subscribe({
      next: (offerSets) => {
        if (this.isCurrentAccountLoad(requestedAccountId, loadSequence)) {
          this.eligibleOfferSets = this.buildEligibleOfferSetOptions(offerSets);
          this.buildOfferSetOfferTextMap();
          this.eligibleOfferSetsLoadedAccountId = requestedAccountId;
          this.eligibleOfferSetsLoadFailed = false;
          this.openGeneratedRuleDialogWhenReady();
        }
      },
      error: () => {
        if (this.isCurrentAccountLoad(requestedAccountId, loadSequence)) {
          this.eligibleOfferSets = [];
          this.offerSetOfferTextMap = {};
          this.eligibleOfferSetsLoadedAccountId = null;
          this.eligibleOfferSetsLoadFailed = true;
          this.discardPendingGeneratedRuleDraft();
          this.showLoadError('Failed to load eligible offer sets.');
        }
      }
    });
  }

  /**
   * Loads authoritative Offer Rules for the current account.
   */
  private loadRules(): void {
    const requestedAccountId = this.accountId;
    const loadSequence = this.accountLoadSequence;
    this.tableLoading = true;
    this.offerRuleService.getOfferRules(requestedAccountId).subscribe({
      next: (rules) => {
        if (this.isCurrentAccountLoad(requestedAccountId, loadSequence)) {
          this.initializeLoadedRules(rules);
          this.tableLoading = false;
          if (this.hasLoaded) {
            this.rulesLoadedAccountId = requestedAccountId;
            this.rulesLoadFailed = false;
            this.openGeneratedRuleDialogWhenReady();
          }
          if (!this.hasLoaded) {
            this.rulesLoadedAccountId = null;
            this.rulesLoadFailed = true;
            this.discardPendingGeneratedRuleDraft();
          }
        }
      },
      error: () => {
        if (this.isCurrentAccountLoad(requestedAccountId, loadSequence)) {
          this.tableLoading = false;
          this.hasLoaded = false;
          this.rulesLoadedAccountId = null;
          this.rulesLoadFailed = true;
          this.discardPendingGeneratedRuleDraft();
          this.showLoadError('Failed to load offer rules.');
        }
      }
    });
  }

  /**
   * Checks whether an asynchronous load still targets the active account.
   */
  private isCurrentAccountLoad(accountId: number, loadSequence: number): boolean {
    const isCurrent = this.accountId === accountId && this.accountLoadSequence === loadSequence;

    return isCurrent;
  }

  /**
   * Initializes loaded rules through authoritative state replacement.
   */
  private initializeLoadedRules(rules: OfferRuleModel[]): void {
    this.replaceWithAuthoritativeRules(rules);
  }

  /**
   * Collects invalid identity or concurrency metadata from loaded rules.
   */
  private getLoadedRuleErrors(rules: OfferRuleModel[]): string[] {
    const errors: string[] = [];
    for (const rule of rules) {
      const validId = Number.isInteger(rule.id) && Number(rule.id) > 0;
      const validModified = this.isValidTimestamp(rule.modified);
      if (!validId || !validModified) {
        errors.push('Invalid persisted rule identity.');
      }
    }

    return errors;
  }

  /**
   * Adds a persisted rule to the baseline and clean working state.
   */
  private addLoadedRule(rule: OfferRuleModel): void {
    const persistedId = Number(rule.id);
    const baselineRule = this.cloneRule(rule);
    const clientKey = `${persistedClientKeyPrefix}${persistedId}`;
    const workingRule = this.cloneRule(baselineRule);
    this.baselineById.set(persistedId, baselineRule);
    const editableRow = this.buildEditableRow(clientKey, workingRule, ruleChangeStates.clean);
    this.workingRowsByClientKey.set(clientKey, editableRow);
  }

  /**
   * Adds a submitted rule as a new local working row.
   */
  private addCreatedRule(rule: OfferRuleModel): void {
    const clientKey = this.generateDraftClientKey();
    const createdRule = this.cloneWritableRule(rule);
    createdRule.accountId = this.accountId;
    const editableRow = this.buildEditableRow(clientKey, createdRule, ruleChangeStates.created);
    this.workingRowsByClientKey.set(clientKey, editableRow);
    this.refreshDerivedState();
  }

  /**
   * Applies a submitted edit to the selected working row.
   */
  private applyEditedRule(rule: OfferRuleModel): void {
    if (this.selectedClientKey !== null) {
      const existingRow = this.workingRowsByClientKey.get(this.selectedClientKey);
      if (existingRow) {
        const editedRule = this.mergeEditedRule(existingRow.rule, rule);
        const changeState = this.resolveEditedChangeState(existingRow, editedRule);
        const editableRow = this.buildEditableRow(existingRow.clientKey, editedRule, changeState);
        this.workingRowsByClientKey.set(existingRow.clientKey, editableRow);
        this.refreshDerivedState();
      }
    }
  }

  /**
   * Merges writable edits while preserving persisted identity metadata.
   */
  private mergeEditedRule(existingRule: OfferRuleModel, editedRule: OfferRuleModel): OfferRuleModel {
    const mergedRule = this.cloneWritableRule(editedRule);
    mergedRule.accountId = this.accountId;
    mergedRule.id = existingRule.id;
    mergedRule.created = existingRule.created;
    mergedRule.modified = existingRule.modified;

    return mergedRule;
  }

  /**
   * Derives whether an edited row is new, modified, or restored to clean.
   */
  private resolveEditedChangeState(row: EditableOfferRule, editedRule: OfferRuleModel): RuleChangeState {
    let changeState: RuleChangeState = ruleChangeStates.created;
    if (row.changeState !== ruleChangeStates.created) {
      changeState = ruleChangeStates.updated;
      const persistedId = this.normalizeNumber(row.rule.id);
      let baseline: OfferRuleModel | undefined;
      if (persistedId !== null) {
        baseline = this.baselineById.get(persistedId);
      }
      if (baseline && this.areWritableRulesEqual(baseline, editedRule)) {
        changeState = ruleChangeStates.clean;
      }
    }

    return changeState;
  }

  /**
   * Removes a local draft or marks a persisted row for deletion.
   */
  private applyLocalDelete(clientKey: string): void {
    const row = this.workingRowsByClientKey.get(clientKey);
    if (row) {
      if (row.changeState === ruleChangeStates.created) {
        this.workingRowsByClientKey.delete(clientKey);
      }
      if (row.changeState !== ruleChangeStates.created) {
        const deletedRule = this.cloneRule(row.rule);
        const editableRow = this.buildEditableRow(clientKey, deletedRule, ruleChangeStates.deleted);
        this.workingRowsByClientKey.set(clientKey, editableRow);
      }
      this.refreshDerivedState();
    }
  }

  /**
   * Restores a persisted working row from its authoritative baseline.
   */
  private restorePersistedRow(clientKey: string): void {
    const row = this.workingRowsByClientKey.get(clientKey);
    if (row) {
      const persistedId = this.normalizeNumber(row.rule.id);
      let baseline: OfferRuleModel | undefined;
      if (persistedId !== null) {
        baseline = this.baselineById.get(persistedId);
      }
      if (baseline) {
        const restoredRule = this.cloneRule(baseline);
        const editableRow = this.buildEditableRow(clientKey, restoredRule, ruleChangeStates.clean);
        this.workingRowsByClientKey.set(clientKey, editableRow);
        this.refreshDerivedState();
      }
    }
  }

  /**
   * Builds a working row with its change state and display criteria.
   */
  private buildEditableRow(
    clientKey: string,
    rule: OfferRuleModel,
    changeState: RuleChangeState
  ): EditableOfferRule
  {
    const editableRow: EditableOfferRule = {
      clientKey,
      rule,
      changeState,
      vehicleCriteriaTags: this.getVehicleCriteriaTags(rule)
    };

    return editableRow;
  }

  /**
   * Recomputes lists, filters, change sets, validation, and dirty state.
   */
  private refreshDerivedState(forceDirtyStateEmission = false): void {
    const rows = Array.from(this.workingRowsByClientKey.values());
    this.publicRules = rows.filter((row) => !this.normalizeBoolean(row.rule.isPrivate));
    this.privateRules = rows.filter((row) => this.normalizeBoolean(row.rule.isPrivate));
    this.applyFilters();
    this.deriveChangeSet(rows);
    const hasChanges = rows.some((row) => row.changeState !== ruleChangeStates.clean);
    if (hasChanges !== this.hasUnsavedRuleChanges || forceDirtyStateEmission) {
      this.hasUnsavedRuleChanges = hasChanges;
      this.hasUnsavedRuleChangesChange.emit(hasChanges);
    }
  }

  /**
   * Derives validated create, update, and delete operations from working rows.
   */
  private deriveChangeSet(rows: EditableOfferRule[]): void {
    const changeSet = this.createEmptyChangeSet();
    const errors: string[] = [];
    let operationCount = 0;
    for (const row of rows) {
      if (row.changeState !== ruleChangeStates.clean) {
        operationCount += 1;
        this.addRowOperation(row, changeSet, errors);
      }
    }
    if (operationCount > maximumBulkOperations) {
      errors.push(`A maximum of ${maximumBulkOperations} rule changes can be saved at once.`);
    }
    this.changeSet = changeSet;
    this.changeSetValidation = {
      isValid: errors.length === 0 && operationCount > 0,
      operationCount,
      errors
    };
  }

  /**
   * Adds the operation represented by a changed row to the change set.
   */
  private addRowOperation(row: EditableOfferRule, changeSet: OfferRuleChangeSet, errors: string[]): void {
    if (row.changeState === ruleChangeStates.created) {
      changeSet.creates.push({clientKey: row.clientKey, rule: this.normalizeWritableRule(row.rule)});
    }
    if (row.changeState === ruleChangeStates.updated) {
      this.addPersistedUpdate(row, changeSet, errors);
    }
    if (row.changeState === ruleChangeStates.deleted) {
      this.addPersistedDelete(row, changeSet, errors);
    }
  }

  /**
   * Adds an update with authoritative identity and concurrency metadata.
   */
  private addPersistedUpdate(row: EditableOfferRule, changeSet: OfferRuleChangeSet, errors: string[]): void {
    const baseline = this.getBaselineForRow(row);
    if (baseline && baseline.id && baseline.modified) {
      changeSet.updates.push({
        id: baseline.id,
        modified: baseline.modified,
        rule: this.normalizeWritableRule(row.rule)
      });
    }
    if (!baseline || !baseline.id || !baseline.modified) {
      errors.push(`Modified rule ${row.clientKey} is missing authoritative identity data.`);
    }
  }

  /**
   * Adds a deletion with authoritative identity and concurrency metadata.
   */
  private addPersistedDelete(row: EditableOfferRule, changeSet: OfferRuleChangeSet, errors: string[]): void {
    const baseline = this.getBaselineForRow(row);
    if (baseline && baseline.id && baseline.modified) {
      changeSet.deletes.push({id: baseline.id, modified: baseline.modified});
    }
    if (!baseline || !baseline.id || !baseline.modified) {
      errors.push(`Deleted rule ${row.clientKey} is missing authoritative identity data.`);
    }
  }

  /**
   * Finds the authoritative baseline associated with a working row.
   */
  private getBaselineForRow(row: EditableOfferRule): OfferRuleModel | undefined {
    let baseline: OfferRuleModel | undefined;
    const persistedId = this.normalizeNumber(row.rule.id);
    if (persistedId !== null) {
      baseline = this.baselineById.get(persistedId);
    }

    return baseline;
  }

  /**
   * Creates an isolated copy of the current bulk change set.
   */
  private cloneChangeSet(changeSet: OfferRuleChangeSet): OfferRuleChangeSet {
    const clonedChangeSet: OfferRuleChangeSet = {
      creates: changeSet.creates.map((create) => ({
        clientKey: create.clientKey,
        rule: this.cloneBulkRuleData(create.rule)
      })),
      updates: changeSet.updates.map((update) => ({
        id: update.id,
        modified: update.modified,
        rule: this.cloneBulkRuleData(update.rule)
      })),
      deletes: changeSet.deletes.map((deleteOperation) => ({
        id: deleteOperation.id,
        modified: deleteOperation.modified
      }))
    };

    return clonedChangeSet;
  }

  /**
   * Copies writable values for an isolated bulk operation.
   */
  private cloneBulkRuleData(rule: BulkOfferRuleData): BulkOfferRuleData {
    const clonedRule: BulkOfferRuleData = {
      offerSetId: rule.offerSetId,
      year: rule.year,
      make: rule.make,
      model: rule.model,
      trim: rule.trim,
      maxSalePrice: rule.maxSalePrice,
      vehicleLimit: rule.vehicleLimit,
      vehicleCondition: rule.vehicleCondition,
      dollarAmountDiscount: rule.dollarAmountDiscount,
      status: rule.status,
      isPrivate: rule.isPrivate
    };

    return clonedRule;
  }

  /**
   * Applies the current search and type filters to both rule lists.
   */
  private applyFilters(): void {
    this.filteredPublicRules = this.filterRows(this.publicRules);
    this.filteredPrivateRules = this.filterRows(this.privateRules);
  }

  /**
   * Filters rows against the current type and normalized search value.
   */
  private filterRows(rows: EditableOfferRule[]): EditableOfferRule[] {
    const normalizedSearch = this.searchValue.trim().toLowerCase();
    const filteredRows = rows.filter((row) => {
      const matchesType = this.rowMatchesRuleType(row);
      const matchesSearch = this.rowMatchesSearch(row, normalizedSearch);

      return matchesType && matchesSearch;
    });

    return filteredRows;
  }

  /**
   * Checks whether a row matches the selected rule type.
   */
  private rowMatchesRuleType(row: EditableOfferRule): boolean {
    let matches = true;
    const rowType = this.getRuleTypeFromRule(row.rule);
    if (this.selectedRuleTypeFilter !== ruleTypeAll) {
      matches = rowType === this.selectedRuleTypeFilter;
    }

    return matches;
  }

  /**
   * Checks searchable rule values for the normalized search term.
   */
  private rowMatchesSearch(row: EditableOfferRule, normalizedSearch: string): boolean {
    let matches = true;
    if (normalizedSearch !== '') {
      const values = [
        this.getOfferSetName(row.rule),
        row.rule.year,
        row.rule.make,
        row.rule.model,
        row.rule.trim,
        row.rule.vehicleCondition,
        row.rule.status,
        this.getChangeStateText(row.changeState)
      ];
      const searchableValue = values.map((value) => String(value ?? '')).join(' ').toLowerCase();
      matches = searchableValue.includes(normalizedSearch);
    }

    return matches;
  }

  /**
   * Determines whether a rule is an Offer Set or dollar discount rule.
   */
  private getRuleTypeFromRule(rule: OfferRuleModel): string {
    let ruleType = ruleTypeOfferSet;
    if (this.normalizeNumber(rule.dollarAmountDiscount) !== null) {
      ruleType = ruleTypeDiscount;
    }

    return ruleType;
  }

  /**
   * Copies writable values and persisted rule metadata.
   */
  private cloneRule(rule: OfferRuleModel): OfferRuleModel {
    const clone = this.cloneWritableRule(rule);
    clone.accountId = rule.accountId;
    clone.id = rule.id;
    clone.created = rule.created;
    clone.modified = rule.modified;

    return clone;
  }

  /**
   * Copies normalized writable values into an Offer Rule model.
   */
  private cloneWritableRule(rule: OfferRuleModel): OfferRuleModel {
    const normalized = this.normalizeWritableRule(rule);
    const clone = new OfferRuleModel();
    clone.accountId = this.accountId;
    clone.offerSetId = normalized.offerSetId;
    clone.year = normalized.year;
    clone.make = normalized.make;
    clone.model = normalized.model;
    clone.trim = normalized.trim;
    clone.maxSalePrice = normalized.maxSalePrice;
    clone.vehicleLimit = normalized.vehicleLimit;
    clone.vehicleCondition = normalized.vehicleCondition;
    clone.dollarAmountDiscount = normalized.dollarAmountDiscount;
    clone.status = normalized.status;
    clone.isPrivate = normalized.isPrivate;

    return clone;
  }

  /**
   * Normalizes writable rule values for comparison and persistence.
   */
  private normalizeWritableRule(rule: OfferRuleModel): NormalizedWritableOfferRule {
    const normalizedRule: NormalizedWritableOfferRule = {
      offerSetId: this.normalizeNumber(rule.offerSetId),
      year: this.normalizeNumber(rule.year),
      make: this.normalizeString(rule.make),
      model: this.normalizeString(rule.model),
      trim: this.normalizeString(rule.trim),
      maxSalePrice: this.normalizeNumber(rule.maxSalePrice),
      vehicleLimit: this.normalizeNumber(rule.vehicleLimit),
      vehicleCondition: this.normalizeCondition(rule.vehicleCondition),
      dollarAmountDiscount: this.normalizeNumber(rule.dollarAmountDiscount),
      status: this.normalizeStatus(rule.status),
      isPrivate: this.normalizeBoolean(rule.isPrivate)
    };

    return normalizedRule;
  }

  /**
   * Compares normalized writable values while ignoring persisted metadata.
   */
  private areWritableRulesEqual(firstRule: OfferRuleModel, secondRule: OfferRuleModel): boolean {
    const first = this.normalizeWritableRule(firstRule);
    const second = this.normalizeWritableRule(secondRule);
    const equal = first.offerSetId === second.offerSetId
      && first.year === second.year
      && first.make === second.make
      && first.model === second.model
      && first.trim === second.trim
      && first.maxSalePrice === second.maxSalePrice
      && first.vehicleLimit === second.vehicleLimit
      && first.vehicleCondition === second.vehicleCondition
      && first.dollarAmountDiscount === second.dollarAmountDiscount
      && first.status === second.status
      && first.isPrivate === second.isPrivate;

    return equal;
  }

  /**
   * Converts a finite numeric value to its normalized representation.
   */
  private normalizeNumber(value: number | string | null | undefined): number | null {
    let normalizedValue: number | null = null;
    const hasValue = value !== null && value !== undefined && value !== '';
    if (hasValue) {
      const numberValue = Number(value);
      if (Number.isFinite(numberValue)) {
        normalizedValue = numberValue;
      }
    }

    return normalizedValue;
  }

  /**
   * Trims a string and converts blank values to null.
   */
  private normalizeString(value: string | null | undefined): string | null {
    let normalizedValue: string | null = null;
    if (value !== null && value !== undefined) {
      const trimmedValue = value.trim();
      if (trimmedValue !== '') {
        normalizedValue = trimmedValue;
      }
    }

    return normalizedValue;
  }

  /**
   * Maps a condition to a supported canonical value.
   */
  private normalizeCondition(value: string | null | undefined): string {
    let condition = 'Any';
    const normalizedValue = this.normalizeString(value);
    if (normalizedValue !== null) {
      const conditionMap: {[key: string]: string} = {
        new: 'New',
        used: 'Used',
        cpo: 'CPO',
        any: 'Any'
      };
      const mappedCondition = conditionMap[normalizedValue.toLowerCase()];
      if (mappedCondition) {
        condition = mappedCondition;
      }
    }

    return condition;
  }

  /**
   * Maps a status to Active or the safe Inactive default.
   */
  private normalizeStatus(value: string | null | undefined): string {
    let status = 'Inactive';
    if (value === 'Active') {
      status = 'Active';
    }

    return status;
  }

  /**
   * Normalizes nullable boolean values to strict true or false.
   */
  private normalizeBoolean(value: boolean | null | undefined): boolean {
    const normalizedValue = value === true;

    return normalizedValue;
  }

  /**
   * Generates a unique temporary key for a new local rule.
   */
  private generateDraftClientKey(): string {
    let clientKey = '';
    do {
      clientKey = `${draftClientKeyPrefix}${this.createCryptographicUuid()}`;
    } while (this.workingRowsByClientKey.has(clientKey));

    return clientKey;
  }

  /**
   * Generates a cryptographically random version 4 UUID.
   */
  private createCryptographicUuid(): string {
    const bytes = new Uint8Array(uuidByteLength);
    globalThis.crypto.getRandomValues(bytes);
    bytes[uuidVersionIndex] = (bytes[uuidVersionIndex] & uuidVersionMask) | uuidVersionValue;
    bytes[uuidVariantIndex] = (bytes[uuidVariantIndex] & uuidVariantMask) | uuidVariantValue;
    const segments: string[] = [];
    let byteIndex = 0;
    for (const segmentLength of uuidSegmentLengths) {
      segments.push(this.buildUuidSegment(bytes, byteIndex, segmentLength));
      byteIndex += segmentLength;
    }
    const uuid = segments.join('-');

    return uuid;
  }

  /**
   * Encodes a UUID byte segment as padded hexadecimal text.
   */
  private buildUuidSegment(bytes: Uint8Array, startIndex: number, length: number): string {
    let segment = '';
    const endIndex = startIndex + length;
    for (let index = startIndex; index < endIndex; index += 1) {
      segment += bytes[index].toString(16).padStart(2, '0');
    }

    return segment;
  }

  /**
   * Checks whether persisted concurrency metadata is a valid timestamp.
   */
  private isValidTimestamp(value: string | undefined): boolean {
    let isValid = false;
    if (value) {
      isValid = !Number.isNaN(new Date(value).getTime());
    }

    return isValid;
  }

  /**
   * Adds a non-empty, non-wildcard criterion to the display tags.
   */
  private addCriteriaTag(tags: string[], value: string | number | null | undefined): void {
    const hasValue = value !== null && value !== undefined && value !== '' && value !== 'Any';
    if (hasValue) {
      tags.push(String(value));
    }
  }

  /**
   * Copies eligible Offer Sets and prepares their display titles.
   */
  private buildEligibleOfferSetOptions(offerSets: EligibleOfferSetModel[]): EligibleOfferSetModel[] {
    const options = offerSets.map((offerSet) => {
      const option = {...offerSet};
      option.displayTitle = offerSet.title;

      return option;
    });

    return options;
  }

  /**
   * Indexes formatted Offer Set payment text for table and modal display.
   */
  private buildOfferSetOfferTextMap(): void
  {
    const textMap: {[key: number]: string} = {};
    for (const offerSet of this.eligibleOfferSets) {
      const hasOfferSetId = offerSet.id !== null && offerSet.id !== undefined;
      if (hasOfferSetId) {
        const offerText = this.composeOfferText(offerSet);
        if (offerText !== '') {
          textMap[offerSet.id] = offerText;
        }
      }
    }
    this.offerSetOfferTextMap = textMap;
  }

  /**
   * Composes supported Offer Set payments in their established display order.
   */
  private composeOfferText(offerSet: EligibleOfferSetModel): string
  {
    let offerText = '';
    const parts: string[] = [];
    const offers = this.resolveOfferSetOffers(offerSet);
    const leaseTexts = this.buildLeaseTexts(offers);
    const financeText = this.buildSingleFinanceText(offers);
    const aprText = this.buildSingleAprText(offers);
    if (leaseTexts.standard !== '') {
      parts.push(leaseTexts.standard);
    }
    if (leaseTexts.alternate !== '') {
      parts.push(leaseTexts.alternate);
    }
    if (financeText !== '') {
      parts.push(financeText);
    }
    if (aprText !== '') {
      parts.push(aprText);
    }
    if (parts.length > 0) {
      offerText = parts.join(' | ');
    }

    return offerText;
  }

  /**
   * Resolves the structured payments available for an Offer Set.
   */
  private resolveOfferSetOffers(offerSet: EligibleOfferSetModel): OfferSetOfferModel[]
  {
    let offers: OfferSetOfferModel[] = [];
    const hasOffers = offerSet.offers !== undefined && offerSet.offers !== null;
    if (hasOffers && offerSet.offers) {
      offers = offerSet.offers;
    }

    return offers;
  }

  /**
   * Builds the first valid standard and alternate lease payment texts.
   */
  private buildLeaseTexts(offers: OfferSetOfferModel[]): {standard: string; alternate: string}
  {
    let standardLease = '';
    let alternateLease = '';
    for (const offer of offers) {
      const isLease = offer.type === 'lease';
      if (isLease) {
        const leaseText = this.buildLeaseText(offer);
        const hasLeaseText = leaseText !== '';
        const isAlternateLease = offer.leaseType !== undefined
          && offer.leaseType !== null
          && offer.leaseType !== 'lease';
        if (hasLeaseText && isAlternateLease && alternateLease === '') {
          alternateLease = leaseText;
        }
        if (hasLeaseText && !isAlternateLease && standardLease === '') {
          standardLease = leaseText;
        }
      }
    }
    const leaseTexts = {standard: standardLease, alternate: alternateLease};

    return leaseTexts;
  }

  /**
   * Builds the first valid finance payment text.
   */
  private buildSingleFinanceText(offers: OfferSetOfferModel[]): string
  {
    let financeText = '';
    for (const offer of offers) {
      const isFinance = offer.type === 'finance';
      if (isFinance && financeText === '') {
        financeText = this.buildFinanceText(offer);
      }
    }

    return financeText;
  }

  /**
   * Builds the first valid APR payment text.
   */
  private buildSingleAprText(offers: OfferSetOfferModel[]): string
  {
    let aprText = '';
    for (const offer of offers) {
      const isApr = offer.type === 'apr';
      if (isApr && aprText === '') {
        aprText = this.buildAprText(offer);
      }
    }

    return aprText;
  }

  /**
   * Formats a standard or alternate lease payment.
   */
  private buildLeaseText(offer: OfferSetOfferModel): string
  {
    let leaseText = '';
    let label = 'Lease';
    const isAlternateLease = offer.leaseType !== undefined
      && offer.leaseType !== null
      && offer.leaseType !== 'lease';
    if (isAlternateLease) {
      label = 'Alternate Lease';
    }
    const amount = this.formatCurrencyValue(offer.value);
    if (amount !== '') {
      leaseText = label + ' for ' + amount + '/mo';
    }

    return leaseText;
  }

  /**
   * Formats a finance payment.
   */
  private buildFinanceText(offer: OfferSetOfferModel): string
  {
    let financeText = '';
    const amount = this.formatCurrencyValue(offer.value);
    if (amount !== '') {
      financeText = 'Finance for ' + amount + '/mo';
    }

    return financeText;
  }

  /**
   * Formats an APR offer using its APR value or payment value fallback.
   */
  private buildAprText(offer: OfferSetOfferModel): string
  {
    let aprText = '';
    let aprValue = this.formatAprValue(offer.apr);
    if (aprValue === '') {
      aprValue = this.formatAprValue(offer.value);
    }
    if (aprValue !== '') {
      aprText = aprValue + '% APR';
    }

    return aprText;
  }

  /**
   * Formats a numeric payment value as whole-dollar USD.
   */
  private formatCurrencyValue(value: unknown): string
  {
    let formatted = '';
    const numericValue = this.resolveNumberValue(value);
    if (numericValue !== null) {
      const transformed = this.currencyPipe.transform(numericValue, 'USD', 'symbol', '1.0-0');
      if (transformed !== null) {
        formatted = transformed;
      }
    }

    return formatted;
  }

  /**
   * Formats a numeric APR value with at most two decimal places.
   */
  private formatAprValue(value: unknown): string
  {
    let formatted = '';
    const numericValue = this.resolveNumberValue(value);
    if (numericValue !== null) {
      formatted = numericValue.toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 2});
    }

    return formatted;
  }

  /**
   * Resolves supported numeric and numeric-string payment values.
   */
  private resolveNumberValue(value: unknown): number | null
  {
    let resolvedValue: number | null = null;
    const isNumber = typeof value === 'number';
    if (isNumber && !Number.isNaN(value as number)) {
      resolvedValue = value as number;
    }
    const isString = typeof value === 'string';
    if (resolvedValue === null && isString) {
      const converted = Number(value);
      const isValidNumber = !Number.isNaN(converted);
      if (isValidNumber) {
        resolvedValue = converted;
      }
    }

    return resolvedValue;
  }

  /**
   * Shows an account-scoped rule loading failure.
   */
  private showLoadError(detail: string): void {
    this.messageService.add({severity: 'error', summary: 'Error', detail});
  }

  /**
   * Creates an empty local create, update, and delete change set.
   */
  private createEmptyChangeSet(): OfferRuleChangeSet {
    const changeSet: OfferRuleChangeSet = {creates: [], updates: [], deletes: []};

    return changeSet;
  }

  /**
   * Creates the initial invalid change-set validation state.
   */
  private createInitialValidation(): OfferRuleChangeSetValidation {
    const validation: OfferRuleChangeSetValidation = {isValid: false, operationCount: 0, errors: []};

    return validation;
  }
}
